import { useState, useEffect, useCallback, useRef } from 'react';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = [],
  shouldFetch: boolean = true
): UseApiState<T> & { refetch: () => void } {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  // Usar ref para prevenir bucles infinitos
  const isLoadingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const fetchData = useCallback(async () => {
    // PREVENIR BUCLE INFINITO - Solo hacer fetch si no está ya cargando
    if (isLoadingRef.current) {
      console.warn('⚠️ useApi: Intento de fetch mientras ya está cargando - IGNORADO');
      return;
    }

    isLoadingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const data = await apiCall();
      setState({ data, loading: false, error: null });
    } catch (error: any) {
      let errorMessage = 'Error desconocido';
      
      if (error?.response) {
        // Error del servidor
        const status = error.response.status;
        if (status === 401) {
          errorMessage = 'No autorizado. Por favor, inicia sesión nuevamente.';
        } else if (status === 403) {
          errorMessage = 'Sin permisos para acceder a este recurso.';
        } else if (status === 404) {
          errorMessage = 'Recurso no encontrado.';
        } else if (status >= 500) {
          errorMessage = 'Error del servidor. Intenta nuevamente más tarde.';
        } else {
          errorMessage = error.response.data?.message || `Error ${status}`;
        }
      } else if (error?.request) {
        // Error de red
        errorMessage = 'Error de conexión. Verifica tu internet y que el servidor esté disponible.';
      } else {
        errorMessage = error.message || 'Error desconocido';
      }
      
      console.error('useApi error:', error);
      setState({ data: null, loading: false, error: errorMessage });
    } finally {
      isLoadingRef.current = false;
    }
  }, [apiCall]);

  useEffect(() => {
    if (shouldFetch && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchData();
    } else if (!shouldFetch) {
      // Si no debe hacer fetch, resetear a estado inicial sin carga
      setState({ data: null, loading: false, error: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

export function useAsyncOperation<T>(): {
  execute: (operation: () => Promise<T>) => Promise<T | null>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = async (operation: () => Promise<T>): Promise<T | null> => {
    try {
      setLoading(true);
      setError(null);
      const result = await operation();
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { execute, loading, error };
}